import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent } from "./components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  Phone,
  Mail,
  MapPin,
  ShieldCheck,
  Star,
  Sparkles,
  Building2,
  Home,
  Menu,
  X,
  Clock,
  Award,
  Users,
  CheckCircle,
  Heart,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav" data-testid="navbar">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex items-center gap-2" data-testid="logo">
            <Sparkles className="w-8 h-8 text-blue-500" />
            <span className="text-xl lg:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Isher Maintenance
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => scrollToSection("services")}
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-services"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection("about")}
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-about"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection("testimonials")}
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-testimonials"
            >
              Testimonials
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
              data-testid="nav-contact"
            >
              Contact
            </button>
          </div>

          {/* Phone & CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:6047044617"
              className="flex items-center gap-2 text-slate-700 hover:text-blue-600 transition-colors"
              data-testid="phone-link-nav"
            >
              <Phone className="w-4 h-4" />
              <span className="font-semibold">(604) 704-4617</span>
            </a>
            <Button
              onClick={() => scrollToSection("contact")}
              className="btn-secondary rounded-full"
              data-testid="nav-quote-btn"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 animate-fadeIn" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <button
                onClick={() => scrollToSection("services")}
                className="text-left px-4 py-2 text-slate-600 hover:text-slate-900 font-medium"
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection("about")}
                className="text-left px-4 py-2 text-slate-600 hover:text-slate-900 font-medium"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection("testimonials")}
                className="text-left px-4 py-2 text-slate-600 hover:text-slate-900 font-medium"
              >
                Testimonials
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="text-left px-4 py-2 text-slate-600 hover:text-slate-900 font-medium"
              >
                Contact
              </button>
              <a
                href="tel:6047044617"
                className="flex items-center gap-2 px-4 py-2 text-blue-600 font-semibold"
              >
                <Phone className="w-4 h-4" />
                (604) 704-4617
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="hero-gradient pt-24 lg:pt-32 pb-16 lg:pb-24" data-testid="hero-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="animate-slideInLeft">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <ShieldCheck className="w-4 h-4" />
              Fully Insured & Bonded
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="hero-title"
            >
              Professional Cleaning for{" "}
              <span className="text-blue-500">Homes & Businesses</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-xl">
              Trusted by renowned businesses across the Lower Mainland. Experience spotless spaces with Isher Maintenance – your partner in cleanliness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                onClick={scrollToContact}
                className="btn-primary text-lg px-8 py-4"
                data-testid="hero-quote-btn"
              >
                Request Free Quote
              </Button>
              <a
                href="tel:6047044617"
                className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-slate-900 text-slate-900 rounded-full font-semibold hover:bg-slate-900 hover:text-white transition-all duration-200"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Now: (604) 704-4617
              </a>
            </div>
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              <div className="trust-item">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium text-slate-700">100% Satisfaction</span>
              </div>
              <div className="trust-item">
                <Clock className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-medium text-slate-700">Same-Day Service</span>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="animate-slideInRight delay-200">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80"
                alt="Professional cleaning service"
                className="hero-image w-full h-[400px] lg:h-[500px] object-cover"
                data-testid="hero-image"
              />
              {/* Floating Badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Serving Lower Mainland</p>
                    <p className="font-bold text-slate-900">10+ Years Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Elderly Promo Banner
const PromoBanner = () => {
  return (
    <section className="promo-banner py-6" data-testid="promo-banner">
      <div className="container-custom">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Heart className="w-8 h-8 text-amber-600" />
          <p className="text-lg sm:text-xl font-semibold text-amber-900">
            <span className="text-2xl font-bold">$50 OFF</span> for Elderly Clients – We Care for Our Community
          </p>
          <Heart className="w-8 h-8 text-amber-600 hidden sm:block" />
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Building2,
      title: "Commercial Cleaning",
      description: "Offices, retail spaces, strata buildings, and more. Keep your business spotless and welcoming.",
      features: ["Office Buildings", "Retail Stores", "Strata Properties", "Medical Facilities"],
      span: "lg:col-span-2",
    },
    {
      icon: Home,
      title: "Residential Cleaning",
      description: "Deep cleaning, regular maintenance, move-in/out cleaning for your home.",
      features: ["Deep Cleaning", "Regular Maintenance", "Move-in/out", "Post-Construction"],
      span: "lg:col-span-1",
    },
  ];

  return (
    <section id="services" className="section-padding bg-white" data-testid="services-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="inline-block text-blue-500 font-semibold mb-4">OUR SERVICES</span>
          <h2
            className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="services-title"
          >
            Cleaning Solutions for Every Need
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From commercial properties to residential homes, we deliver exceptional cleaning services tailored to your requirements.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <Card
              key={index}
              className={`service-card ${service.span} group cursor-pointer`}
              data-testid={`service-card-${index}`}
            >
              <CardContent className="p-8">
                <div className="feature-icon mb-6 group-hover:bg-blue-100 transition-colors">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-6">{service.description}</p>
                <ul className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const features = [
    {
      icon: ShieldCheck,
      title: "Fully Insured",
      description: "Complete peace of mind with comprehensive insurance coverage for all our services.",
    },
    {
      icon: Award,
      title: "Trusted by Businesses",
      description: "Working with renowned businesses across the Lower Mainland for years.",
    },
    {
      icon: Users,
      title: "Experienced Team",
      description: "Professional, trained, and background-checked cleaning specialists.",
    },
    {
      icon: Star,
      title: "Quality Guaranteed",
      description: "100% satisfaction guarantee on every cleaning job we complete.",
    },
  ];

  return (
    <section id="about" className="section-padding bg-slate-50" data-testid="about-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <img
              src="https://images.unsplash.com/photo-1527515637462-cff94eecc1ac?w=800&q=80"
              alt="Vancouver skyline - Lower Mainland"
              className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              data-testid="about-image"
            />
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <span className="inline-block text-blue-500 font-semibold mb-4">WHY CHOOSE US</span>
            <h2
              className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="about-title"
            >
              Your Trusted Cleaning Partner in Lower Mainland
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              At Isher Maintenance, we take pride in delivering exceptional cleaning services that exceed expectations. Our commitment to quality and customer satisfaction has made us a preferred choice for businesses and homeowners across the region.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="flex gap-4" data-testid={`feature-${index}`}>
                  <div className="feature-icon shrink-0">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{feature.title}</h4>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Sarah M.",
      role: "Homeowner, Vancouver",
      content: "Isher Maintenance transformed my home! Their attention to detail is incredible. I've been using their services for over a year now.",
      rating: 5,
    },
    {
      name: "David K.",
      role: "Office Manager, Burnaby",
      content: "Reliable, professional, and thorough. Our office has never looked better. Highly recommend their commercial cleaning services!",
      rating: 5,
    },
    {
      name: "Maria L.",
      role: "Property Manager, Surrey",
      content: "We trust Isher Maintenance with all our strata properties. Their team is always punctual and delivers excellent results.",
      rating: 5,
    },
  ];

  return (
    <section id="testimonials" className="section-padding bg-white" data-testid="testimonials-section">
      <div className="container-custom">
        <div className="text-center mb-16">
          <span className="inline-block text-blue-500 font-semibold mb-4">TESTIMONIALS</span>
          <h2
            className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="testimonials-title"
          >
            What Our Clients Say
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Don't just take our word for it – hear from our satisfied customers across the Lower Mainland.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card
              key={index}
              className="testimonial-card"
              data-testid={`testimonial-card-${index}`}
            >
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="font-semibold text-blue-600">{testimonial.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! We'll contact you shortly.");
      setFormData({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.success("Thank you! We'll contact you shortly.");
      setFormData({ name: "", phone: "", email: "", service: "", message: "" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="section-padding bg-slate-900" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div>
            <span className="inline-block text-blue-400 font-semibold mb-4">GET IN TOUCH</span>
            <h2
              className="text-3xl lg:text-4xl font-bold text-white mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="contact-title"
            >
              Ready for a Spotless Space?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Contact us today for a free quote. We're here to help keep your home or business sparkling clean.
            </p>

            <div className="space-y-6 mb-8">
              <a
                href="tel:6047044617"
                className="flex items-center gap-4 text-white hover:text-blue-400 transition-colors group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Call Us Anytime</p>
                  <p className="text-xl font-semibold">(604) 704-4617</p>
                </div>
              </a>

              <a
                href="mailto:ishermaintenance@gmail.com"
                className="flex items-center gap-4 text-white hover:text-blue-400 transition-colors group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Email Us</p>
                  <p className="text-xl font-semibold">ishermaintenance@gmail.com</p>
                </div>
              </a>

              <div className="flex items-center gap-4 text-white" data-testid="contact-location">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Service Area</p>
                  <p className="text-xl font-semibold">Lower Mainland, BC</p>
                </div>
              </div>
            </div>

            {/* Reminder */}
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-200 font-medium flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Remember: $50 OFF for Elderly Clients!
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="bg-white shadow-2xl" data-testid="contact-form-card">
            <CardContent className="p-8">
              <h3
                className="text-2xl font-bold text-slate-900 mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Request a Free Quote
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <Input
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="contact-phone-input"
                  />
                  <Input
                    name="email"
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    data-testid="contact-email-input"
                  />
                </div>
                <Select
                  value={formData.service}
                  onValueChange={(value) => setFormData({ ...formData, service: value })}
                >
                  <SelectTrigger className="form-input" data-testid="contact-service-select">
                    <SelectValue placeholder="Select Service Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="commercial">Commercial Cleaning</SelectItem>
                    <SelectItem value="residential">Residential Cleaning</SelectItem>
                    <SelectItem value="deep-clean">Deep Cleaning</SelectItem>
                    <SelectItem value="move-in-out">Move-in/out Cleaning</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea
                  name="message"
                  placeholder="Tell us about your cleaning needs..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="form-input resize-none"
                  data-testid="contact-message-input"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-secondary py-4 text-lg"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? "Sending..." : "Get Free Quote"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 py-12" data-testid="footer">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-blue-400" />
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Isher Maintenance
            </span>
          </div>

          {/* Contact */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <a
              href="tel:6047044617"
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
              data-testid="footer-phone"
            >
              <Phone className="w-4 h-4" />
              (604) 704-4617
            </a>
            <span className="text-slate-500 hidden md:inline">|</span>
            <a
              href="mailto:ishermaintenance@gmail.com"
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4" />
              ishermaintenance@gmail.com
            </a>
            <span className="text-slate-500 hidden md:inline">|</span>
            <span className="text-slate-300">Lower Mainland, BC</span>
          </div>

          {/* Copyright */}
          <p className="text-slate-500 text-sm">
            © {currentYear} Isher Maintenance. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />
      <Navbar />
      <main>
        <HeroSection />
        <PromoBanner />
        <ServicesSection />
        <WhyChooseUsSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
